/**
 * Blocs de chargement.
 *
 * Jusqu'ici, une page affichait son état vide en attendant ses données :
 * « Aucun dossier pour l'instant » s'affichait une fraction de seconde sur une
 * liste qui en contenait cinq. Sur une connexion lente — un commercial en
 * déplacement — le message reste plusieurs secondes et devient un mensonge.
 *
 * Un squelette dit la vérité : « quelque chose arrive, voici sa forme ». Il
 * évite aussi le sursaut de la page qui se remplit d'un coup, puisque la place
 * est déjà prise.
 *
 * L'animation est définie dans src/index.css (une image-clé ne s'écrit pas
 * dans un attribut style) et s'efface pour qui a demandé à réduire les
 * animations dans son système.
 */
import React from 'react';
import { C, E, RAYON } from '../theme';

/** Bloc gris de dimensions libres. */
export function Bloc({ largeur = '100%', hauteur = 12, rayon = RAYON.sm, style }) {
  return (
    <div className="squelette" style={{
      width: largeur, height: hauteur, borderRadius: rayon,
      background: '#e9ebef', ...style,
    }} />
  );
}

/** Rangée de cartes d'indicateur, à la forme de celles du tableau de bord. */
export function SqueletteIndicateurs({ nombre = 4, colonnes }) {
  return (
    <div style={colonnes}>
      {Array.from({ length: nombre }, (_, i) => (
        <div key={i} style={{
          background: C.surface, border: `1px solid ${C.bordure}`,
          borderLeft: `3px solid ${C.bordure}`, borderRadius: RAYON.lg,
          padding: `${E.md} ${E.lg}`, display: 'flex', flexDirection: 'column',
          gap: E.sm,
        }}>
          <Bloc largeur="55%" hauteur={10} />
          <Bloc largeur="40%" hauteur={22} />
          <Bloc largeur="75%" hauteur={9} />
        </div>
      ))}
    </div>
  );
}

/** Lignes de tableau, alignées sur le nombre de colonnes affichées. */
export function SqueletteTableau({ lignes = 6, colonnes = 5 }) {
  return (
    <tbody>
      {Array.from({ length: lignes }, (_, i) => (
        <tr key={i} style={{ borderBottom: `1px solid ${C.bordure}` }}>
          {Array.from({ length: colonnes }, (_, j) => (
            <td key={j} style={{ padding: `${E.md} ${E.lg}` }}>
              {/* Largeurs inégales : des blocs tous identiques font grille de
                  mots croisés, pas tableau en cours de chargement. */}
              <Bloc largeur={['70%', '55%', '45%', '65%', '35%'][j % 5]} hauteur={11} />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}

/** Zone rectangulaire, pour un graphique ou une carte. */
export function SqueletteBloc({ hauteur = 220 }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: E.md,
                  height: hauteur, padding: `${E.lg} 0` }}>
      {[45, 70, 35, 85, 55, 65].map((h, i) => (
        <Bloc key={i} largeur="100%" hauteur={`${h}%`} />
      ))}
    </div>
  );
}

export default Bloc;
